<script setup lang="ts">
import '@/utils/floorUtils.ts';

import { computed, onMounted, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { InlineSearchBar } from '@/components/utilities/search';
import BuildingFacadeCarousel from '@/components/purchase/BuildingFacadeCarousel.vue';
import HeaderWithBackButton from '@/components/utilities/HeaderWithBackButton.vue';
import UnitCanvasDialogModal from '@/components/dialogs/UnitCanvasDialogModal.vue';
import UnitDatatable from '@/components/tables/UnitDatatable.vue';
import UnitList from '@/components/rental/units/UnitList.vue';
import UnitSelectionWizardSteps from '@/components/forms/UnitSelectionWizardSteps.vue';
import ViewModeToggleButton from '@/components/buttons/ViewModeToggleButton.vue';
import { useBuildingFacadesApi } from '@/composables/useBuildingFacadesApi';
import { useRentalFilters } from '@/composables/useRentalFilters';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useUnit } from '@/composables/useUnit';
import { useUnitsApi } from '@/composables/useUnitsApi';
import { DEFAULT_RENTAL_UNIT_STATUSES } from '@/constants/rentalConstants';
import { ListingTypeEnum } from '@/enums/ListingType';
import { UnitStatusEnum } from '@/enums/UnitStatus';
import type { AvailableLocales, LocaleSchema } from '@/locales/index';
import type { Building } from '@/typings/buildings/Building';
import type { BuildingFacade } from '@/typings/buildings/BuildingFacade';
import type { FacadeRenderLayer } from '@/typings/drawing/FacadeRenderLayer';
import type { Region } from '@/typings/regions/Region';
import type { GetUnitsRequest } from '@/typings/requests/GetUnitsRequest';
import type { Unit } from '@/typings/units/Unit';
import { DefaultUnitsFilters, type UnitsFilters } from '@/typings/units/UnitsFilters';

const { getBuildingFacades, getBuildingFacadesLoading } = useBuildingFacadesApi();
const { getUnitsByBuildingId, getUnitsByBuildingIdLoading } = useUnitsApi();
const { sortUnitsByStatus } = useUnit();
const { t, locale } = useI18n<{ message: LocaleSchema }, AvailableLocales>({
  useScope: 'global',
});

const INITIAL_UNITS_COUNT = 18;

const displayedUnitsCount = ref(INITIAL_UNITS_COUNT);

const { getRentalUnitWizardUrl, getProjectWizardUrlFromBuilding } = useRentalLocalizedUrls();
const { loadFiltersFromRentalStorage } = useRentalFilters();

// Détection de la largeur d'écran pour afficher le bon bouton
const isMobile = ref(window.innerWidth < 768);

const props = defineProps<{
  building: Building;
  region?: Region;
}>();

const buildingId = ref<number>(props.building.id);
const buildingFacades = ref<BuildingFacade[]>([]);
const hoveredUnit = ref<Unit>();
const selectedFacade = ref<BuildingFacade>();
// Unités louées comprises, comme les vendues en achat : en rouge sur la façade, en bas de la liste
const units = ref<Unit[]>([]);
const wizardStep = ref(4);
const showUnitModal = ref(false);
const modalUnit = ref<Unit>();

// Charger le mode d'affichage depuis localStorage, par défaut 'table'
const VIEW_MODE_STORAGE_KEY = 'rental-browsing-view-mode';
const savedViewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY) as 'cards' | 'table' | null;
const viewMode = ref<'cards' | 'table'>('table');
// const viewMode = ref<'cards' | 'table'>(savedViewMode || 'table');

// Sauvegarder le mode d'affichage dans localStorage quand il change
watch(viewMode, (newMode) => {
  localStorage.setItem(VIEW_MODE_STORAGE_KEY, newMode);
});

const unitsFilters = ref<UnitsFilters>({ ...DefaultUnitsFilters });

const filterCount = computed(() => {
  return Object.values(unitsFilters.value).reduce((acc: number, filter) => {
    if (Array.isArray(filter)) {
      return acc + filter.length;
    }
    
    return acc + (filter ? 1 : 0);
  }, 0);
});

const remainingUnitsCount = computed(() => Math.max(0, units.value.length - displayedUnitsCount.value));
const hasMoreUnits = computed(() => remainingUnitsCount.value > 0);
const loadMoreUnits = () => {
  displayedUnitsCount.value = units.value.length;
};

function onFacadeSelect(facade: BuildingFacade) {
  selectedFacade.value = facade;
}

function onUnitLayerClick(layer: any) {
  const facadeLayer = layer as FacadeRenderLayer;
  const unit = units.value.find(u => u.id === facadeLayer.unitId);
  if (unit) {
    modalUnit.value = unit;
    showUnitModal.value = true;
  }
}

function onUnitLayerHover(layer: any | undefined) {
  if (!layer) {
    hoveredUnit.value = undefined;
    return;
  }
  
  const facadeLayer = layer as FacadeRenderLayer;
  const unit = units.value.find(u => u.id === facadeLayer.unitId);
  if (unit) {
    hoveredUnit.value = unit;
  }
}

const loadUnits = async () => {
  if (buildingId.value) {
    try {
      const allFilters = loadFiltersFromRentalStorage();
      
      const unitRequest: GetUnitsRequest = {
        locale: locale.value?.toString().substring(0, 2) || 'fr',
        listingTypes: [ListingTypeEnum.Rental],
        statuses: [...DEFAULT_RENTAL_UNIT_STATUSES, UnitStatusEnum.Rented],
        unitConfigurations: allFilters.unitConfigurations && allFilters.unitConfigurations.length > 0 ? allFilters.unitConfigurations : undefined,
        availabilityDates: allFilters.availabilityDates,
        dogAllowed: allFilters.dogAllowed,
        floorNumbers: allFilters.floors,
        minPrice: allFilters.minPrice,
        maxPrice: allFilters.maxPrice
      };
      
      const unitsData = await getUnitsByBuildingId(buildingId.value, unitRequest);
      units.value = unitsData;

      // Réinitialiser le compteur d'unités affichées
      displayedUnitsCount.value = INITIAL_UNITS_COUNT;
    } catch (error) {
      console.error('Failed to reload units with filters:', error);
    }
  }
};

// Fonction pour appliquer les filtres secondaires
const applySecondaryFilters = async () => {
  // Recharger les unités avec les nouveaux filtres
  await loadUnits();
  // Synchroniser les filtres locaux pour afficher les nouveaux résultats
  syncFiltersFromStorage();
};

function onUnitSelected(unit: Unit) {
  window.location.href = getRentalUnitWizardUrl(unit);
}

// Navigation handlers
function onBackToBuildingSelection() {
  window.location.href = getProjectWizardUrlFromBuilding(props.building);
}

// Fonction pour synchroniser les filtres locaux avec ceux du localStorage
const syncFiltersFromStorage = () => {
  // Les unités sont déjà filtrées côté serveur par loadUnits(), 
  // donc on réinitialise les filtres locaux pour afficher toutes les unités reçues
  unitsFilters.value = { ...DefaultUnitsFilters };
};

onMounted(async () => {
  // Écouter les changements de taille d'écran
  const handleResize = () => {
    isMobile.value = window.innerWidth < 768;
  };
  window.addEventListener('resize', handleResize);

  if (buildingId.value) {
    try {
      // Synchroniser les filtres
      syncFiltersFromStorage();
      
      // Charger les façades et les unités en parallèle
      const [facadesData] = await Promise.all([
        getBuildingFacades(buildingId.value),
        loadUnits()
      ]);

      buildingFacades.value = facadesData;
      
      // Sélectionner automatiquement la première façade
      const sortedFacades = [...buildingFacades.value].sort((a, b) => a.facadeOrder - b.facadeOrder);
      if (sortedFacades.length > 0) {
        selectedFacade.value = sortedFacades[0];
      }
    } catch (error) {
      console.error('Failed to load building data:', error);
    }
  } else {
    syncFiltersFromStorage();
    await loadUnits();
  }
});

const filteredUnits = computed(() => units.value);

provide('app-header-height', 120);
provide('filterCount', filterCount);
</script>

<template>
  <!--Location - Étape 4 - Sélection de l'unité-->
  <div class="building-rental-browsing">
    <!-- Barre de filtres -->
    <InlineSearchBar action-type="filter"
                     :selected-region="props.building.regionId"
                     @filtersApplied="applySecondaryFilters" />
    <div class="wrapper wrapper_large">
      <!-- Étapes du wizard -->
      <UnitSelectionWizardSteps v-model="wizardStep" />
 
      <!-- Contenu principal -->
      <div class="main-content">
        <div class="heading-and-toggle">
          <!--En-tête avec bouton de retour-->
          <HeaderWithBackButton :title="`${t('browsingApp.units.chooseTheRentalUnitDescription', { count: units.length, project: props.building.localizedProjectName })} - ${t('browsingApp.buildings.buildingNumber')} ${props.building.no}`"
                                :subtitle="t('browsingApp.units.chooseYourUnitFromBuildingDescription')"
                                @back-click="onBackToBuildingSelection">
            <template v-if="!isMobile" #actions>
              <!-- Toggle pour changer le mode d'affichage -->
              <ViewModeToggleButton v-model="viewMode" />
            </template>
          </HeaderWithBackButton>
        </div>
        <!-- Layout with unit cards on left and carousel on right -->
        <div class="rental-browsing-layout">
          <!-- Unit cards section -->
          <div class="rental-browsing-units">
            <!-- Trier AVANT de couper : les unités louées restent en bas, au-delà de la première page aussi.
                 sortUnitsByStatus appelle useI18n : elle doit rester dans le gabarit, pas dans un computed. -->
            <!-- Mode cartes -->
            <UnitList v-if="viewMode === 'cards'"
                      v-model:hovered="hoveredUnit"
                      mode="rental"
                      :units="sortUnitsByStatus(filteredUnits).slice(0, displayedUnitsCount)"
                      :loading="getUnitsByBuildingIdLoading"
                      @unit-selected="onUnitSelected" />

            <!-- Mode tableau -->
            <UnitDatatable v-else
                           v-model:hovered="hoveredUnit"
                           mode="rental"
                           :units="sortUnitsByStatus(filteredUnits).slice(0, displayedUnitsCount)"
                           @unit-click="onUnitSelected" />

            <!-- Bouton "Voir plus d'unités" -->
            <div v-if="hasMoreUnits"
                 class="loadMoreUnits">
              <button
                type="button"
                class="btn"
                @click="loadMoreUnits">
                {{ t('browsingApp.units.loadMoreUnits', { count: remainingUnitsCount }, remainingUnitsCount) }}
              </button>
            </div>
            <!-- Toggle pour changer le mode d'affichage (mobile) -->
            <ViewModeToggleButton v-if="isMobile"
                                  v-model="viewMode" />
          </div>

          <!-- Building facade carousel section -->
          <div v-if="buildingFacades.length"
               class="rental-browsing-carousel">
            <BuildingFacadeCarousel v-model:hovered-unit="hoveredUnit"
                                    :building-facades="buildingFacades"
                                    :units="units"
                                    :selected-facade="selectedFacade"
                                    @facade-select="onFacadeSelect"
                                    @unit-layer-click="onUnitLayerClick"
                                    @unit-layer-hover="onUnitLayerHover" />
          </div>
        </div>


        <UnitCanvasDialogModal v-model:open="showUnitModal"
                               :unit="modalUnit"
                               :building="props.building"
                               @unit-selected="onUnitSelected"
                               @reset-unit-modal="modalUnit = undefined" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.rental-browsing-layout{
  padding: 0 0 var(--spc48-24) 0;
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.rental-browsing-units {
  width: 65%;
}

.rental-browsing-carousel {
  width: 35%;
  min-height: 500px;
  position:sticky;
  top: 230px;
}

.loadMoreUnits {
  display: flex;
  justify-content: center;
  margin: var(--spc48-24) 0;

  .btn{
    background-color: #28a745;
    border-color: #28a745;
    color: white;

    &:hover {
      background-color: #218838;
      border-color: #1e7e34;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
    }
  }
}

/* Render canvas styles for facades */
.renderCanvas_facade {
  --baseColor: var(--gray-100);
}

.renderCanvas_facade [data-unit-status="Available"] {
  --baseColor: var(--clrGreenStatus);
}

.renderCanvas_facade [data-unit-status="Reserved"] {
  --baseColor: var(--clrOrangeStatus);
}

.renderCanvas_facade [data-unit-status="Sold"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-unit-status="Rented"] {
  --baseColor: var(--clrRedStatus);
}

.renderCanvas_facade [data-unit-status="Upcoming"] {
  --baseColor: var(--clrYellowStatus);
}

@media screen and (max-width: 1024px){
  .rentalFiltersBarRelativeContainer{
    top:6.6rem;
  }
  
  .rental-browsing-layout {
    flex-direction: column-reverse;
    gap: var(--spc48-24);

    .rental-browsing-carousel {
      width: 100%;
      min-height: auto;
      position:relative;
      top: 0;
    }
  }

  .rental-browsing-units{
    width:100%;
  }
  .rental-browsing-carousel{
    width:100%;
    min-height: auto;
  }
}
@media screen and (max-width:767px) {
  .rental-browsing-units{
    display:flex;
    flex-direction: column;
    gap:var(--spc24-16);
  }
}
</style>